import React, { useState, useEffect } from 'react';
import { IS_RECMAPPER } from './product';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { AuthPage, CompleteRegistrationForm } from './AuthComponents';
import { ForgotPassword, ResetPassword } from './components/ForgotPassword';
import { ProjectDashboard } from './ProjectDashboard';
import { AdminDashboard } from './AdminDashboard';
import { CommandCenter } from './CommandCenter';
import { CRM } from './CRM';
import Onboarding from './Onboarding';
import { CompanyDetailPage } from './crm/CompanyDetailPage';
import { ContactDetailPage } from './crm/ContactDetailPage';
import AppShell from './AppShell';
import { TourProvider } from './tour/TourProvider';
import { hasSearch } from './utils/searchAccess';
import { BackgroundJobsProvider } from './BackgroundJobsContext';
import Overview from './Overview';
import RecMapperApp from './RecMapperApp';
import DataEnrichment from './DataEnrichment';
import ExtractionTrainer from './ExtractionTrainer';
import MarketIntelligence from './MarketIntelligence';
import MapData from './MapData';
import V2Lab from './V2Lab';
import PitchDecks from './PitchDecks';
import TeamManagement from './TeamManagement';
import AuditLogs from './AuditLogs';
import { LoadScript } from '@react-google-maps/api';
import InvitationAccept from './InvitationAccept';
import Settings from './Settings';
import EmbedView from './EmbedView';
import OnboardingIntake from './OnboardingIntake';
import CampaignsIndex from './CampaignsIndex';
import CampaignWorkspace from './CampaignWorkspace';
import LegacyCampaignRedirect from './LegacyCampaignRedirect';
import AtsLayout from './ats/AtsLayout';
import AtsOverview from './ats/AtsOverview';
import AtsClients from './ats/AtsClients';
import AtsClientDetail from './ats/AtsClientDetail';
import AtsRoles from './ats/AtsRoles';
import AtsRolePipeline from './ats/AtsRolePipeline';
import AtsPeople from './ats/AtsPeople';
import AtsPersonDetail from './ats/AtsPersonDetail';
import AtsPlacements from './ats/AtsPlacements';
import AtsTasks from './ats/AtsTasks';
import AtsNotes from './ats/AtsNotes';
import AtsReports from './ats/AtsReports';
import AtsDeals from './ats/AtsDeals';
import AtsInbox from './ats/AtsInbox';
import AtsCalendar from './ats/AtsCalendar';
import SearchComingSoon from './SearchComingSoon';
import ContactSearch from './ContactSearch';
import AtsComingSoon from './AtsComingSoon';
import Training from './Training';
import TrainingDetail from './TrainingDetail';

import VincereDashboard from './VincereDashboard';



import MappingPricing, { PublicMappingPricing } from './MappingPricing';
import './App.css';

const AppContent: React.FC = () => {
  const { user, isLoadingUser } = useAuth();

  // Show loading while checking auth - prevents flash of login page on Stripe redirect
  if (isLoadingUser) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes - no auth required */}
      <Route path="/invitation/:token" element={<InvitationAccept />} />
      <Route path="/complete-registration" element={<CompleteRegistrationForm />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/embed/:projectId" element={<EmbedView />} />
      <Route path="/onboarding/intake/:token" element={<OnboardingIntake />} />
      <Route path="/public/pricing" element={<PublicMappingPricing />} />

      {/* All other routes require authentication */}
      <Route path="/*" element={
        !user ? <AuthPage /> : <AuthenticatedRoutes />
      } />
    </Routes>
  );
};

const AuthenticatedRoutes: React.FC = () => {
  const { user } = useAuth();

  return (
    <BackgroundJobsProvider>
    <TourProvider>
    <AppShell>
      <Routes>
        {/* Dashboard (was Overview tab) */}
        <Route path="/" element={<Overview onNavigate={(view) => {}} />} />

        {/* Projects (was Projects tab) */}
        <Route path="/projects" element={<ProjectDashboard />} />

        {/* Project workspace — keeps its own internal tab system */}
        <Route path="/project/:projectId" element={<ProjectRoute />} />

        {/* Everything below up to Team is MarketMapr-only. */}
        {!IS_RECMAPPER && (<>
        {/* Extraction Trainer — teach the company-extraction judge by example */}
        <Route path="/extraction/:projectId" element={<ExtractionTrainer />} />

        {/* CRM — Global Entity Views (Phase 3) */}
        <Route path="/jobs" element={<Navigate to="/campaigns?mode=delivery" replace />} />
        <Route path="/jobs/:legacyId" element={<LegacyCampaignRedirect source="crm_job" />} />
        <Route path="/campaigns" element={<CampaignsIndex />} />
        <Route path="/campaigns/:campaignId" element={<CampaignWorkspace />} />

        {/* Search: the full candidates+companies search (the V2 Lab
            experience, Sample|Universe toggle included) for user 1 while
            staged; the teaser for everyone else. /hm-search is the slimmer
            hiring-manager surface a project's Companies tab seeds. */}
        <Route path="/search" element={<SearchRoute />} />
        <Route path="/contact-search" element={<ContactSearchRoute />} />
        <Route path="/hm-search" element={<Navigate to="/contact-search" replace />} />
        {/* ATS/CRM teaser for everyone; the live module is /ats (user 1) */}
        <Route path="/ats-soon" element={<AtsComingSoon />} />

        {/* Native ATS/CRM module (user-1 gated server-side; see routes/ats.js) */}
        <Route path="/ats" element={<AtsLayout />}>
          <Route index element={<AtsOverview />} />
          <Route path="reports" element={<AtsReports />} />
          <Route path="candidates" element={<AtsPeople mode="candidate" />} />
          <Route path="contacts" element={<AtsPeople mode="contact" />} />
          <Route path="deals" element={<AtsDeals />} />
          <Route path="roles" element={<AtsRoles />} />
          <Route path="roles/:roleId" element={<AtsRolePipeline />} />
          <Route path="clients" element={<AtsClients />} />
          <Route path="clients/:clientId" element={<AtsClientDetail />} />
          <Route path="people" element={<Navigate to="/ats/candidates" replace />} />
          <Route path="people/:personId" element={<AtsPersonDetail />} />
          <Route path="placements" element={<AtsPlacements />} />
          <Route path="inbox" element={<AtsInbox />} />
          <Route path="tasks" element={<AtsTasks />} />
          <Route path="notes" element={<AtsNotes />} />
          <Route path="calendar" element={<AtsCalendar />} />
        </Route>
        <Route path="/vincere" element={<VincereDashboard />} />

        {/* Tools (were MainDashboard tabs) */}
        <Route path="/enrichment" element={<DataEnrichment />} />
        <Route path="/intelligence" element={<MarketIntelligence />} />
        <Route path="/mapdata" element={<MapData />} />
        <Route path="/v2lab" element={<V2Lab />} />
        <Route path="/prospect-pages" element={<PitchDecks />} />

        </>)}

        {/* Team */}
        <Route path="/team" element={<TeamManagement />} />
        <Route path="/team/audit" element={<AuditLogs />} />

        {/* Settings */}
        <Route path="/settings" element={<Settings />} />
        {!IS_RECMAPPER && <Route path="/pricing" element={<MappingPricing />} />}
        <Route path="/subscription" element={<Navigate to="/settings" replace />} />

        {/* Training */}
        <Route path="/training" element={<Training />} />
        <Route path="/training/:cardId" element={<TrainingDetail />} />

        {/* Admin */}
        <Route
          path="/admin"
          element={
            user?.role === 'admin' ? (
              <AdminDashboard />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {!IS_RECMAPPER && (<>
        <Route
          path="/admin/command-center"
          element={
            user?.role === 'admin' ? (
              <CommandCenter />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/admin/crm"
          element={
            user?.role === 'admin' ? (
              <CRM />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/admin/onboarding"
          element={
            user?.role === 'admin' ? (
              <Onboarding />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/admin/crm/companies/:id"
          element={
            user?.role === 'admin' ? (
              <CompanyDetailPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/admin/crm/contacts/:id"
          element={
            user?.role === 'admin' ? (
              <ContactDetailPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        </>)}

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
    </TourProvider>
    </BackgroundJobsProvider>
  );
};

// Placeholder component for upcoming CRM features
const ComingSoon: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className="flex flex-col items-center justify-center py-24">
    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center mb-6">
      <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    </div>
    <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
    <p className="text-gray-500 text-center max-w-md">{description}</p>
  </div>
);

const SearchRoute: React.FC = () => {
  const { user } = useAuth();
  return hasSearch(user) ? <V2Lab universeOnly /> : <SearchComingSoon />;
};

// Contact Search: the people surface seeded from a project's companies.
const ContactSearchRoute: React.FC = () => {
  const { user } = useAuth();
  return hasSearch(user) ? <ContactSearch /> : <SearchComingSoon />;
};

// Component to handle project selection from URL
const ProjectRoute: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { projects, selectedProject, selectProject, isLoadingProjects, isLoadingUser } = useAuth();
  const navigate = useNavigate();
  const [isSelecting, setIsSelecting] = useState(false);

  useEffect(() => {
    // Wait for both user and projects to load
    if (isLoadingProjects || isLoadingUser) return;

    if (!projectId) {
      navigate('/');
      return;
    }

    if (projects.length === 0) {
      navigate('/');
      return;
    }

    const project = projects.find(p => p.id === parseInt(projectId));

    if (project) {
      if (!selectedProject || selectedProject.id !== project.id) {
        setIsSelecting(true);
        selectProject(project);
        setTimeout(() => setIsSelecting(false), 100);
      } else {
        setIsSelecting(false);
      }
    } else {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projects, isLoadingProjects, isLoadingUser, navigate]);

  if (isLoadingProjects || isLoadingUser || isSelecting) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">
            {isSelecting ? 'Loading project view...' : 'Loading project...'}
          </p>
        </div>
      </div>
    );
  }

  if (!selectedProject) {
    if (projectId && projects.length > 0) {
      const project = projects.find(p => p.id === parseInt(projectId));
      if (project) {
        return (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading project...</p>
            </div>
          </div>
        );
      }
    }
    return <Navigate to="/" replace />;
  }

  return <RecMapperApp />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
          <AppContent />
        </LoadScript>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
